import { useConfetti } from "@prismicio/editor-support/Animation"
import { useOnChange } from "@prismicio/editor-support/React"
import { AnimatedElement } from "@prismicio/editor-ui"
import { type ReactNode, useCallback, useRef, useState } from "react"

import type {
  OnboardingState,
  OnboardingStep,
  OnboardingStepId,
  OnboardingTracking,
} from "../service"
import { OnboardingCard } from "./components/OnboardingCard"
import { getOnboardingContent } from "./content"

export interface OnboardingGuideProps {
  onboardingState: OnboardingState
  tracking: OnboardingTracking
  onToggleStep: (stepId: OnboardingStepId) => Promise<string[]>
  onToggleGuide: () => Promise<void>
  fallback?: ReactNode
  /** This allows consumers of the onboarding to do something else for certain steps other than opening the modal. */
  stepOverrides?: Partial<Record<OnboardingStepId, () => void>>
  repositoryName?: string
  projectName?: string
}

const confettiDuration = 3000
const fadeOutOffset = 400

export function OnboardingGuide(props: OnboardingGuideProps) {
  const {
    onboardingState,
    tracking,
    onToggleStep,
    onToggleGuide,
    fallback = null,
    stepOverrides,
    repositoryName,
    projectName,
  } = props
  const { context, isDismissed } = onboardingState

  const { steps, defaultCompletedStepIds, tutorialLink } = getOnboardingContent(
    { ...context, repositoryName, projectName },
  )
  const completedSteps = mergeCompletedSteps(onboardingState.completedSteps)
  const animation = useConfettiFadeoutAnimation(dismiss, fadeOutOffset)

  useOnChange(isDismissed, () => !isDismissed && animation.reset())

  // currently we don't have content that makes sense for other frameworks
  if (context.framework === "other") return <>{fallback}</>

  function dismiss() {
    void onToggleGuide()
  }

  async function handleToggleStep(step: OnboardingStep) {
    const completedSteps = await onToggleStep(step.id)

    if (completedSteps.includes(step.id)) {
      void tracking.track({
        event: "shared-onboarding:step-completed",
        stepId: step.id,
        stepTitle: step.title,
        source: tracking.source,
      })
    }

    if (mergeCompletedSteps(completedSteps).length === steps.length) {
      void tracking.track({
        event: "shared-onboarding:completed",
        source: tracking.source,
      })
      animation.start()
    }
  }

  function mergeCompletedSteps(completedSteps: string[]) {
    const stepsIds = steps.map(({ id }) => id)
    return Array.from(
      new Set([
        ...completedSteps.filter((id) =>
          stepsIds.includes(id as OnboardingStepId),
        ),
        ...defaultCompletedStepIds,
      ]),
    )
  }

  if (isDismissed) return null

  return (
    <AnimatedElement enterDuration={0} exitDuration={fadeOutOffset}>
      {animation.status !== "hidden" && (
        <div ref={animation.refs.container} style={{ position: "relative" }}>
          <OnboardingCard
            steps={steps}
            completedSteps={completedSteps}
            tutorialLink={tutorialLink}
            tracking={tracking}
            onToggleStep={handleToggleStep}
            onClose={animation.status !== "animating" ? dismiss : undefined}
            stepOverrides={stepOverrides}
          />
          <div
            ref={animation.refs.cannon}
            style={{ position: "absolute", bottom: 0, left: 0 }}
          />
        </div>
      )}
    </AnimatedElement>
  )
}

type AnimationStatus = "animating" | "hidden" | "visible"

/**
 * @param onAnimationEnd - Callback called after the confetti animation ends.
 * @param fadeOutOffset - How soon (in ms) the status will change to `hidden` before the confetti animation ends. e.g. If set to 400, the status will change to `hidden` 400ms before the confetti animation ends.
 */
function useConfettiFadeoutAnimation(
  onAnimationEnd: () => void,
  fadeOutOffset = 400,
) {
  const confetti = useConfetti({ config: { duration: confettiDuration } })
  const [status, setStatus] = useState<AnimationStatus>("visible")
  const isAnimationEndTriggered = useRef(false)

  const fadeOut = useCallback(() => {
    setStatus("animating")
    setTimeout(() => setStatus("hidden"), confettiDuration - fadeOutOffset)

    if (!isAnimationEndTriggered.current) {
      setTimeout(onAnimationEnd, confettiDuration)
      isAnimationEndTriggered.current = true
    }
  }, [onAnimationEnd, fadeOutOffset])

  const start = () => {
    confetti.throwConfetti()
    fadeOut()
  }

  const reset = useCallback(() => {
    setStatus("visible")
    isAnimationEndTriggered.current = false
  }, [])

  return {
    refs: {
      container: confetti.confettiContainerRef,
      cannon: confetti.confettiCannonRef,
    },
    status,
    start,
    reset,
  }
}
